import { supabase } from '../../config/supabase';
import { Alert, AlertFormData, RawAlert } from '../../types/alert';
import { alertHistoryService } from './alertHistory.service';
import logger from '../../utils/logger';

/**
 * Convertit un objet alerte du format Supabase vers le format de l'application
 * @param data Données de l'alerte au format Supabase
 * @returns Alerte au format de l'application
 */
function toAppFormat(data: RawAlert): Alert {
  return {
    id: data.id,
    title: data.title,
    description: data.description,
    dueDate: new Date(data.due_date),
    status: data.status,
    priority: data.priority,
    relatedEntityType: data.related_entity_type,
    relatedEntityId: data.related_entity_id,
    isRecurring: data.is_recurring,
    recurrencePattern: data.recurrence_pattern,
    recurrenceEndDate: data.recurrence_end_date ? new Date(data.recurrence_end_date) : undefined,
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
    createdBy: data.created_by,
    notificationSent: data.notification_sent,
    notificationSentAt: data.notification_sent_at ? new Date(data.notification_sent_at) : undefined
  };
}

/**
 * Convertit un objet alerte du format de l'application vers le format Supabase
 * @param alert Données de l'alerte au format de l'application
 * @returns Données de l'alerte au format Supabase
 */
function toSupabaseFormat(alert: AlertFormData): Record<string, any> {
  return {
    title: alert.title,
    description: alert.description || null,
    due_date: alert.dueDate.toISOString(),
    status: alert.status || 'pending',
    priority: alert.priority || 'normal',
    related_entity_type: alert.relatedEntityType,
    related_entity_id: alert.relatedEntityId,
    is_recurring: alert.isRecurring || false,
    recurrence_pattern: alert.recurrencePattern || null,
    recurrence_end_date: alert.recurrenceEndDate ? alert.recurrenceEndDate.toISOString() : null,
    created_by: alert.createdBy || null,
    notification_sent: false
  };
}

/**
 * Vérifie si un ID est temporaire (commence par 'temp_')
 * @param id ID à vérifier
 * @returns true si l'ID est temporaire, false sinon
 */
function isTemporaryId(id: string): boolean {
  return id.startsWith('temp_');
}

/**
 * Récupère les alertes temporaires depuis Supabase
 * @param entityId ID de l'entité associée
 * @returns Promesse qui résout avec un tableau d'alertes temporaires
 */
async function getTemporaryAlertsByEntityId(entityId: string): Promise<Alert[]> {
  try {
    logger.debug(`Récupération des alertes temporaires pour l'entité ${entityId}`);
    
    const { data, error } = await supabase
      .from('alerts')
      .select('*')
      .eq('related_entity_id', entityId)
      .eq('is_temporary', true);
    
    if (error) {
      logger.error('Erreur lors de la récupération des alertes temporaires:', error);
      throw error;
    }
    
    const alerts = (data || []).map(toAppFormat);
    logger.debug(`${alerts.length} alertes temporaires récupérées pour l'entité ${entityId}`);
    
    return alerts;
  } catch (error) {
    logger.error('Erreur lors de la récupération des alertes temporaires:', error);
    return [];
  }
}

/**
 * Crée une alerte temporaire dans Supabase
 * @param entityId ID de l'entité associée
 * @param alertData Données de l'alerte
 * @returns Promesse qui résout avec l'alerte créée
 */
async function createTemporaryAlert(entityId: string, alertData: AlertFormData): Promise<Alert | null> {
  try {
    logger.debug(`Création d'une alerte temporaire pour l'entité ${entityId}`);
    
    // Préparer les données pour Supabase
    const supabaseData = {
      ...toSupabaseFormat(alertData),
      is_temporary: true,
      related_entity_id: entityId
    };
    
    // Insérer l'alerte dans Supabase
    const { data, error } = await supabase
      .from('alerts')
      .insert([supabaseData])
      .select()
      .single();
    
    if (error) {
      logger.error('Erreur lors de la création de l\'alerte temporaire:', error);
      throw error;
    }
    
    if (!data) {
      logger.error('Aucune donnée retournée après la création de l\'alerte temporaire');
      return null;
    }
    
    const alert = toAppFormat(data);
    logger.debug(`Alerte temporaire créée avec succès pour l'entité ${entityId}:`, alert);
    
    return alert;
  } catch (error) {
    logger.error('Erreur lors de la création de l\'alerte temporaire:', error);
    return null;
  }
}

/**
 * Confirme les alertes temporaires pour une entité
 * @param oldId ID temporaire de l'entité
 * @param newId ID permanent de l'entité
 * @returns Promesse qui résout avec un tableau des alertes confirmées
 */
async function confirmTemporaryAlerts(oldId: string, newId: string): Promise<Alert[]> {
  try {
    logger.debug(`Confirmation des alertes temporaires: oldId=${oldId}, newId=${newId}`);
    
    // Récupérer les alertes temporaires pour l'entité
    const tempAlerts = await getTemporaryAlertsByEntityId(oldId);
    
    if (tempAlerts.length === 0) {
      logger.debug(`Aucune alerte temporaire trouvée pour l'entité ${oldId}`);
      return [];
    }
    
    // Mettre à jour chaque alerte pour la rendre permanente et associée au nouvel ID
    const confirmedAlerts: Alert[] = [];
    
    for (const alert of tempAlerts) {
      const { data, error } = await supabase
        .from('alerts')
        .update({
          is_temporary: false,
          related_entity_id: newId
        })
        .eq('id', alert.id)
        .select()
        .single();
      
      if (error) {
        logger.error(`Erreur lors de la confirmation de l'alerte ${alert.id}:`, error);
        continue;
      }
      
      if (data) {
        confirmedAlerts.push(toAppFormat(data));
      }
    }
    
    logger.debug(`${confirmedAlerts.length}/${tempAlerts.length} alertes temporaires confirmées pour l'entité ${oldId}`);
    
    return confirmedAlerts;
  } catch (error) {
    logger.error('Erreur lors de la confirmation des alertes temporaires:', error);
    return [];
  }
}

export const alertsService = {
  /**
   * Récupère toutes les alertes
   * @returns Une promesse qui résout avec un tableau d'alertes
   */
  async getAll(): Promise<Alert[]> {
    try {
      const { data, error } = await supabase
        .from('alerts')
        .select('*')
        .order('due_date', { ascending: true });

      if (error) {
        logger.error('Erreur lors de la récupération des alertes:', error);
        throw error;
      }
      
      return (data || []).map(toAppFormat);
    } catch (error) {
      logger.error('Erreur lors de la récupération des alertes:', error);
      throw error;
    }
  },

  /**
   * Récupère les alertes par type d'entité et ID
   * @param entityType Type d'entité (action, contact, marker)
   * @param entityId ID de l'entité
   * @returns Une promesse qui résout avec un tableau d'alertes
   */
  async getByEntity(entityType: string, entityId: string): Promise<Alert[]> {
    try {
      const { data, error } = await supabase
        .from('alerts')
        .select('*')
        .eq('related_entity_type', entityType)
        .eq('related_entity_id', entityId)
        .order('due_date', { ascending: true });

      if (error) {
        logger.error(`Erreur lors de la récupération des alertes pour ${entityType} ${entityId}:`, error);
        throw error;
      }
      
      return (data || []).map(toAppFormat);
    } catch (error) {
      logger.error(`Erreur lors de la récupération des alertes pour ${entityType} ${entityId}:`, error);
      throw error;
    }
  },

  /**
   * Récupère les alertes à venir dans un intervalle de temps donné
   * @param startDate Date de début de l'intervalle
   * @param endDate Date de fin de l'intervalle
   * @returns Une promesse qui résout avec un tableau d'alertes
   */
  async getUpcoming(startDate: Date, endDate: Date): Promise<Alert[]> {
    try {
      const { data, error } = await supabase
        .from('alerts')
        .select('*')
        .gte('due_date', startDate.toISOString())
        .lte('due_date', endDate.toISOString())
        .eq('status', 'pending')
        .order('due_date', { ascending: true });

      if (error) {
        logger.error('Erreur lors de la récupération des alertes à venir:', error);
        throw error;
      }
      
      return (data || []).map(toAppFormat);
    } catch (error) {
      logger.error('Erreur lors de la récupération des alertes à venir:', error);
      throw error;
    }
  },

  /**
   * Crée une nouvelle alerte
   * @param alert Les données de l'alerte à créer
   * @returns Une promesse qui résout avec l'alerte créée
   */
  async create(alert: AlertFormData): Promise<Alert> {
    try {
      logger.debug('Création d\'une alerte:', alert);
      
      const supabaseData = toSupabaseFormat(alert);
      
      const { data, error } = await supabase
        .from('alerts')
        .insert(supabaseData)
        .select()
        .single();
      
      if (error) {
        logger.error('Erreur lors de la création de l\'alerte:', error);
        throw error;
      }
      
      const createdAlert = toAppFormat(data as RawAlert);
      
      // Enregistrer dans l'historique
      try {
        await alertHistoryService.create({
          alertId: createdAlert.id,
          actionType: 'created',
          newStatus: createdAlert.status,
          newData: createdAlert,
          createdBy: createdAlert.createdBy
        });
        logger.debug('Historique de création d\'alerte enregistré avec succès');
      } catch (historyError) {
        logger.error('Erreur lors de l\'enregistrement de l\'historique de création d\'alerte:', historyError);
        // Ne pas bloquer la création de l'alerte si l'enregistrement de l'historique échoue
      }
      
      return createdAlert;
    } catch (error) {
      logger.error('Erreur lors de la création de l\'alerte:', error);
      throw error;
    }
  },

  /**
   * Met à jour une alerte existante
   * @param id L'ID de l'alerte à mettre à jour
   * @param alert Les données à mettre à jour
   * @returns Une promesse qui résout avec l'alerte mise à jour
   */
  async update(id: string, alert: Partial<Alert>): Promise<Alert> {
    try {
      logger.debug(`Mise à jour de l'alerte ${id}:`, alert);
      
      // Récupérer l'alerte existante pour l'historique
      const { data: existingData, error: fetchError } = await supabase
        .from('alerts')
        .select('*')
        .eq('id', id)
        .single();
      
      if (fetchError) {
        logger.error(`Erreur lors de la récupération de l'alerte ${id} pour l'historique:`, fetchError);
        throw fetchError;
      }
      
      const existingAlert = toAppFormat(existingData as RawAlert);
      const isStatusChanged = alert.status && alert.status !== existingAlert.status;
      
      // Préparer les données pour Supabase
      const updateData: Record<string, any> = {};
      
      if (alert.title !== undefined) updateData.title = alert.title;
      if (alert.description !== undefined) updateData.description = alert.description;
      if (alert.status !== undefined) updateData.status = alert.status;
      if (alert.priority !== undefined) updateData.priority = alert.priority;
      
      // Traiter les dates
      if (alert.dueDate) {
        updateData.due_date = alert.dueDate instanceof Date 
          ? alert.dueDate.toISOString() 
          : new Date(alert.dueDate).toISOString();
      }
      
      if (alert.isRecurring !== undefined) updateData.is_recurring = alert.isRecurring;
      if (alert.recurrencePattern !== undefined) updateData.recurrence_pattern = alert.recurrencePattern;
      
      if (alert.recurrenceEndDate) {
        updateData.recurrence_end_date = alert.recurrenceEndDate instanceof Date 
          ? alert.recurrenceEndDate.toISOString() 
          : new Date(alert.recurrenceEndDate).toISOString();
      }
      
      // Mettre à jour l'alerte
      const { data, error } = await supabase
        .from('alerts')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        logger.error(`Erreur lors de la mise à jour de l'alerte ${id}:`, error);
        throw error;
      }
      
      const updatedAlert = toAppFormat(data as RawAlert);
      
      // Enregistrer dans l'historique
      try {
        if (isStatusChanged) {
          // Si le statut a changé, enregistrer un changement de statut
          await alertHistoryService.logStatusChange(
            id,
            existingAlert.status,
            updatedAlert.status,
            updatedAlert.createdBy
          );
          logger.debug(`Historique de changement de statut enregistré pour l'alerte ${id}`);
        } else {
          // Sinon, enregistrer une mise à jour générale
          await alertHistoryService.logUpdate(
            id,
            existingAlert,
            updatedAlert,
            updatedAlert.createdBy
          );
          logger.debug(`Historique de mise à jour enregistré pour l'alerte ${id}`);
        }
      } catch (historyError) {
        logger.error(`Erreur lors de l'enregistrement de l'historique pour l'alerte ${id}:`, historyError);
        // Ne pas bloquer la mise à jour de l'alerte si l'enregistrement de l'historique échoue
      }
      
      return updatedAlert;
    } catch (error) {
      logger.error(`Erreur lors de la mise à jour de l'alerte ${id}:`, error);
      throw error;
    }
  },

  /**
   * Marque une alerte comme complétée
   * @param id L'ID de l'alerte à marquer comme complétée
   * @returns Une promesse qui résout avec l'alerte mise à jour
   */
  async markAsCompleted(id: string): Promise<Alert> {
    try {
      // Récupérer l'alerte existante pour l'historique
      const { data: existingData, error: fetchError } = await supabase
        .from('alerts')
        .select('*')
        .eq('id', id)
        .single();
      
      if (fetchError) {
        logger.error(`Erreur lors de la récupération de l'alerte ${id}:`, fetchError);
        throw fetchError;
      }
      
      const existingAlert = toAppFormat(existingData as RawAlert);
      
      // Mettre à jour le statut
      const { data, error } = await supabase
        .from('alerts')
        .update({ status: 'completed', updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        logger.error(`Erreur lors du marquage de l'alerte ${id} comme complétée:`, error);
        throw error;
      }
      
      const updatedAlert = toAppFormat(data as RawAlert);
      
      // Enregistrer dans l'historique
      try {
        await alertHistoryService.logStatusChange(
          id,
          existingAlert.status,
          'completed',
          updatedAlert.createdBy
        );
        logger.debug(`Historique de complétion enregistré pour l'alerte ${id}`);
      } catch (historyError) {
        logger.error(`Erreur lors de l'enregistrement de l'historique pour l'alerte ${id}:`, historyError);
      }
      
      return updatedAlert;
    } catch (error) {
      logger.error(`Erreur lors du marquage de l'alerte ${id} comme complétée:`, error);
      throw error;
    }
  },

  /**
   * Reporte une alerte à une date ultérieure
   * @param id L'ID de l'alerte à reporter
   * @param newDueDate La nouvelle date d'échéance
   * @returns Une promesse qui résout avec l'alerte mise à jour
   */
  async snooze(id: string, newDueDate: Date): Promise<Alert> {
    try {
      // Récupérer l'alerte existante pour l'historique
      const { data: existingData, error: fetchError } = await supabase
        .from('alerts')
        .select('*')
        .eq('id', id)
        .single();
      
      if (fetchError) {
        logger.error(`Erreur lors de la récupération de l'alerte ${id}:`, fetchError);
        throw fetchError;
      }
      
      const existingAlert = toAppFormat(existingData as RawAlert);
      
      // Mettre à jour la date d'échéance
      const { data, error } = await supabase
        .from('alerts')
        .update({ 
          due_date: newDueDate.toISOString(),
          updated_at: new Date().toISOString() 
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        logger.error(`Erreur lors du report de l'alerte ${id}:`, error);
        throw error;
      }
      
      const updatedAlert = toAppFormat(data as RawAlert);
      
      // Enregistrer dans l'historique
      try {
        await alertHistoryService.logUpdate(
          id,
          existingAlert,
          updatedAlert,
          updatedAlert.createdBy
        );
        logger.debug(`Historique de report enregistré pour l'alerte ${id}`);
      } catch (historyError) {
        logger.error(`Erreur lors de l'enregistrement de l'historique pour l'alerte ${id}:`, historyError);
      }
      
      return updatedAlert;
    } catch (error) {
      logger.error(`Erreur lors du report de l'alerte ${id}:`, error);
      throw error;
    }
  },

  /**
   * Marque une alerte comme ayant envoyé une notification
   * @param id L'ID de l'alerte à mettre à jour
   * @returns Une promesse qui résout avec l'alerte mise à jour
   */
  async markNotificationSent(id: string): Promise<Alert> {
    return this.update(id, { 
      notificationSent: true,
      notificationSentAt: new Date()
    });
  },

  /**
   * Supprime une alerte
   * @param id L'ID de l'alerte à supprimer
   * @returns Une promesse qui résout lorsque la suppression est terminée
   */
  async delete(id: string): Promise<void> {
    try {
      // Récupérer l'alerte avant de la supprimer pour l'historique
      const { data: existingData, error: fetchError } = await supabase
        .from('alerts')
        .select('*')
        .eq('id', id)
        .single();
      
      if (fetchError) {
        logger.error(`Erreur lors de la récupération de l'alerte ${id} avant suppression:`, fetchError);
        // Continuer malgré l'erreur pour permettre la suppression
      } else {
        // Enregistrer dans l'historique avant de supprimer
        try {
          const existingAlert = toAppFormat(existingData as RawAlert);
          await alertHistoryService.create({
            alertId: id,
            actionType: 'deleted',
            previousStatus: existingAlert.status,
            previousData: existingAlert,
            createdBy: existingAlert.createdBy
          });
          logger.debug(`Historique de suppression enregistré pour l'alerte ${id}`);
        } catch (historyError) {
          logger.error(`Erreur lors de l'enregistrement de l'historique pour l'alerte ${id}:`, historyError);
          // Continuer malgré l'erreur pour permettre la suppression
        }
      }
      
      // Supprimer l'alerte
      const { error } = await supabase
        .from('alerts')
        .delete()
        .eq('id', id);
      
      if (error) {
        logger.error(`Erreur lors de la suppression de l'alerte ${id}:`, error);
        throw error;
      }
      
      logger.info(`Alerte ${id} supprimée avec succès`);
    } catch (error) {
      logger.error(`Erreur lors de la suppression de l'alerte ${id}:`, error);
      throw error;
    }
  },

  /**
   * Crée une alerte pour une action
   * @param actionId ID de l'action
   * @param title Titre de l'alerte
   * @param description Description de l'alerte
   * @param dueDate Date d'échéance
   * @param priority Priorité de l'alerte
   * @returns Une promesse qui résout avec l'alerte créée ou null si l'alerte est temporaire
   */
  async createActionAlert(
    actionId: string, 
    title: string, 
    description: string, 
    dueDate: Date, 
    priority: 'low' | 'normal' | 'high' = 'normal'
  ): Promise<Alert | null> {
    const alertData: AlertFormData = {
      title,
      description,
      dueDate,
      priority,
      relatedEntityType: 'action',
      relatedEntityId: actionId
    };
    
    // Vérifier si l'ID de l'action est temporaire
    if (isTemporaryId(actionId)) {
      logger.debug('Création d\'une alerte avec un ID temporaire:', {
        title,
        description,
        dueDate: dueDate.toISOString(),
        tempId: actionId
      });
      
      // Créer l'alerte temporaire dans Supabase
      const tempAlert = await createTemporaryAlert(actionId, alertData);
      
      if (tempAlert) {
        logger.info('✅ Alerte temporaire créée avec succès dans Supabase:', {
          tempId: actionId,
          alertId: tempAlert.id,
          dueDate: tempAlert.dueDate.toISOString()
        });
      } else {
        logger.error('Erreur lors de la création de l\'alerte temporaire dans Supabase:', {
          tempId: actionId
        });
      }
      
      return tempAlert;
    }
    
    // Si l'ID de l'action n'est pas temporaire, créer l'alerte dans Supabase
    try {
      logger.debug('Création d\'une alerte permanente dans Supabase:', {
        actionId,
        title,
        dueDate: dueDate.toISOString()
      });
      
      const alert = await this.create(alertData);
      
      logger.info('✅ Alerte créée avec succès dans Supabase:', {
        alertId: alert.id,
        actionId,
        dueDate: alert.dueDate.toISOString()
      });
      
      return alert;
    } catch (error) {
      logger.error('❌ Erreur lors de la création de l\'alerte dans Supabase:', error);
      throw error;
    }
  },

  /**
   * Migre les alertes temporaires vers Supabase
   * @param oldId ID temporaire
   * @param newId ID permanent
   * @returns Tableau des alertes migrées
   */
  async migrateTemporaryAlerts(oldId: string, newId: string): Promise<Alert[]> {
    try {
      logger.debug('Début de la migration des alertes temporaires:', {
        oldId,
        newId,
        oldIdStartsWithTemp: oldId.startsWith('temp_')
      });
      
      // Vérifier que l'ID est bien temporaire
      if (!oldId.startsWith('temp_')) {
        logger.warn(`L'ID ${oldId} n'est pas un ID temporaire, migration ignorée`);
        return [];
      }
      
      // Confirmer les alertes temporaires pour l'ID temporaire
      const migratedAlerts = await confirmTemporaryAlerts(oldId, newId);
      
      logger.debug(`${migratedAlerts.length} alertes migrées pour l'ID temporaire ${oldId}`);
      
      return migratedAlerts;
    } catch (error) {
      logger.error('Erreur lors de la migration des alertes temporaires:', error);
      return [];
    }
  },

  /**
   * Crée une alerte à partir d'une action
   * @param action L'action à partir de laquelle créer l'alerte
   * @returns Une promesse qui résout avec l'alerte créée
   */
  async createFromAction(action: any): Promise<Alert | null> {
    try {
      // Vérifier si l'action a une date de rappel
      if (!action.reminderDate) {
        logger.debug('Aucune date de rappel pour cette action, pas d\'alerte créée');
        return null;
      }

      // Créer un titre pour l'alerte basé sur le type d'action
      let title = '';
      switch (action.type) {
        case 'call':
          title = 'Rappel d\'appel';
          break;
        case 'visit':
          title = 'Rappel de visite';
          break;
        case 'email':
          title = 'Rappel d\'email';
          break;
        case 'note':
          title = 'Rappel de note';
          break;
        default:
          title = `Rappel: ${action.type}`;
      }

      // Créer une description pour l'alerte
      const description = action.description || 'Aucune description';

      // Utiliser la date de rappel comme date d'échéance
      const dueDate = new Date(action.reminderDate);

      // Déterminer la priorité en fonction du type d'action
      let priority: 'low' | 'normal' | 'high' = 'normal';
      if (action.type === 'call' || action.type === 'visit') {
        priority = 'high';
      } else if (action.type === 'email') {
        priority = 'normal';
      } else {
        priority = 'low';
      }

      // Créer l'alerte pour l'action
      return await this.createActionAlert(
        action.id,
        title,
        description,
        dueDate,
        priority
      );
    } catch (error) {
      logger.error('Erreur lors de la création d\'une alerte à partir d\'une action:', error);
      throw error;
    }
  },

  /**
   * Met à jour ou crée une alerte à partir d'une action
   * @param action L'action à partir de laquelle mettre à jour ou créer l'alerte
   * @returns Une promesse qui résout avec l'alerte mise à jour ou créée
   */
  async updateOrCreateFromAction(action: any): Promise<Alert | null> {
    try {
      // Vérifier si l'action a une date de rappel
      if (!action.reminderDate) {
        logger.debug('Aucune date de rappel pour cette action, pas d\'alerte à mettre à jour');
        return null;
      }

      // Rechercher si une alerte existe déjà pour cette action
      const existingAlerts = await this.getByEntity('action', action.id);

      if (existingAlerts.length > 0) {
        // Mettre à jour l'alerte existante
        const existingAlert = existingAlerts[0];
        
        // Créer un titre pour l'alerte basé sur le type d'action
        let title = '';
        switch (action.type) {
          case 'call':
            title = 'Rappel d\'appel';
            break;
          case 'visit':
            title = 'Rappel de visite';
            break;
          case 'email':
            title = 'Rappel d\'email';
            break;
          case 'note':
            title = 'Rappel de note';
            break;
          default:
            title = `Rappel: ${action.type}`;
        }

        // Mettre à jour l'alerte
        return await this.update(existingAlert.id, {
          title,
          description: action.description || 'Aucune description',
          dueDate: new Date(action.reminderDate),
          status: 'pending' // Réinitialiser le statut si l'action a été mise à jour
        });
      } else {
        // Créer une nouvelle alerte
        return await this.createFromAction(action);
      }
    } catch (error) {
      logger.error('Erreur lors de la mise à jour ou création d\'une alerte à partir d\'une action:', error);
      throw error;
    }
  }
};

